import React, { useState } from "react";
import { Plus } from "lucide-react";

import { Button } from "@/components/primitives/Button";
import { Header } from "@/components/layout/Header";
import { PageShell } from "@/components/layout/PageShell";
import { FlowCard } from "@/components/features/FlowCard";
import { FlowForm } from "@/components/features/FlowForm";
import { useFlows } from "@/hooks/useFlows";
import { Flow, FlowCreate } from "@/types";

export const HomePage: React.FC = () => {
  const {
    flows,
    isLoading,
    error,
    createFlow,
    updateFlow,
    deleteFlow,
    toggleFlow,
  } = useFlows();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);

  const handleCreate = async (flow: FlowCreate) => {
    const created = await createFlow(flow);
    return created !== null;
  };

  const handleUpdate = async (flow: FlowCreate) => {
    if (!editingFlow) return false;
    const updated = await updateFlow(editingFlow.id, flow);
    return updated !== null;
  };

  const handleEdit = (flow: Flow) => {
    setEditingFlow(flow);
    setIsFormOpen(true);
  };

  const handleClose = () => {
    setIsFormOpen(false);
    setEditingFlow(null);
  };

  const handleDelete = async (id: string) => {
    const flow = flows.find((item) => item.id === id);
    const warning = flow?.flow_type === "softnu_checks"
      ? "Delete this flow and all its archived logs? Source logs may already have been removed, so this can delete your only remaining copy. Back up the archive first."
      : "Are you sure you want to delete this flow?";
    if (window.confirm(warning)) {
      await deleteFlow(id);
    }
  };

  return (
    <PageShell>
      <Header
        action={
          <Button onClick={() => setIsFormOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            New flow
          </Button>
        }
      />

      <main className="page-container py-10">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Health flows
            </h2>
            <p className="mt-1 text-slate-500 dark:text-slate-400">
              Monitor your services with scheduled HTTP and database checks.
            </p>
          </div>
          {!isLoading && flows.length > 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {flows.length} flow{flows.length !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20 text-slate-500 dark:text-slate-400">
            Loading flows…
          </div>
        )}

        {error && (
          <div
            className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            {error}
          </div>
        )}

        {!isLoading && flows.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 py-20 text-center dark:border-slate-700 dark:bg-slate-900/30">
            <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
              No flows yet
            </h3>
            <p className="mb-6 max-w-sm text-slate-500 dark:text-slate-400">
              Create your first flow to start monitoring a service.
            </p>
            <Button onClick={() => setIsFormOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Create a flow
            </Button>
          </div>
        )}

        {flows.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {flows.map((flow) => (
              <FlowCard
                key={flow.id}
                flow={flow}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={toggleFlow}
              />
            ))}
          </div>
        )}
      </main>

      <FlowForm
        isOpen={isFormOpen}
        onClose={handleClose}
        onSubmit={editingFlow ? handleUpdate : handleCreate}
        initialFlow={editingFlow}
      />
    </PageShell>
  );
};
